import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Card, EmptyState, Spinner, StatusPill } from '../../components/ui';
import { ApiError, api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { brl, datePt, dateTimePt, statusLabel } from '../../lib/format';
import { usePageMeta } from '../../lib/seo';
import { BillingGate } from './BillingGate';
import { tr } from '../../lib/i18n';

interface ApptRow {
  id: string;
  code: string;
  scheduled_date: string;
  start_time: string;
  end_time: string;
  service_name: string;
  service_duration_minutes: number;
  price_cents: number;
  status: string;
  notes: string | null;
  cancellation_reason: string | null;
  customerName?: string;
  professionalName?: string;
}

interface OverviewResponse {
  today: string;
  todaysAppointments: ApptRow[];
  upcoming: ApptRow[];
  pendingCount: number;
  recentCancellations: {
    id: string;
    code: string;
    service_name: string;
    scheduled_date: string;
    status: string;
    cancellation_reason: string | null;
    updated_at: string;
  }[];
  monthStats: { completed: number; cancelled: number; noShow: number };
  notifications: {
    id: string;
    kind: string;
    title: string;
    body: string;
    link: string | null;
    read_at: string | null;
    created_at: string;
  }[];
}

const TONE: Record<string, 'neutral' | 'positive' | 'warning' | 'danger'> = {
  pending: 'warning',
  confirmed: 'positive',
  completed: 'neutral',
  cancelled_by_customer: 'danger',
  cancelled_by_professional: 'danger',
  no_show: 'danger'
};

export function PanelOverviewPage(): JSX.Element {
  usePageMeta(tr('Visão geral do painel'), tr('Atendimentos de hoje, próximos horários, solicitações pendentes e indicadores do mês.'));
  const { active, refresh } = useAuth();
  const businessId = active?.businessId ?? '';
  const subscription = active?.subscription ?? null;
  // Sem teste vigente ou assinatura ativa a tela inicial vira a escolha do plano.
  const operational = subscription?.status === 'trialing' || subscription?.status === 'active';

  const [data, setData] = useState<OverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async () => {
    if (!businessId || !operational) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<OverviewResponse>(`/api/appointments/${businessId}/overview`);
      setData(res);
    } catch (err) {
      if (err instanceof ApiError && err.status === 402) {
        // O servidor expirou o teste agora; a próxima renderização mostra os planos.
        await refresh();
        return;
      }
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [businessId, operational, refresh]);

  useEffect(() => {
    void load();
  }, [load]);

  if (active && !operational) return <BillingGate />;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">{tr("Visão geral")}</h1>
          <p className="mt-2 text-sm text-muted">
            {active?.businessName ?? ''} — {data ? tr('hoje é {date}', { date: datePt(data.today) }) : tr('carregando data do servidor…')}
          </p>
        </div>
        <div className="flex flex-wrap gap-2.5">
          <Link to="/painel/agenda" className="nl-btn nl-btn-secondary">
            {tr("Abrir agenda")}
          </Link>
          <Link to="/painel/agendamentos" className="nl-btn nl-btn-primary">
            {tr("Gerenciar agendamentos")}
          </Link>
        </div>
      </div>

      {loading ? (
        <Card>
          <Spinner label={tr("Carregando visão geral…")} />
        </Card>
      ) : null}

      {error && !loading ? (
        <OverviewError error={error} onRetry={() => void load()} />
      ) : null}

      {data && !loading ? (
        <>
          <div className="grid grid-cols-2 gap-4">
            <Metric label={tr("Atendimentos hoje")} value={String(data.todaysAppointments.length)} />
            <Metric label={tr("Solicitações pendentes")} value={String(data.pendingCount)} />
            <Metric label={tr("Concluídos no mês")} value={String(data.monthStats.completed)} />
            <Metric label={tr("Cancelamentos no mês")} value={String(data.monthStats.cancelled)} />
            <Metric label={tr("Não comparecimentos no mês")} value={String(data.monthStats.noShow)} />
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            <Card as="section" className="min-w-0">
              <h2 className="mb-3 text-lg font-semibold">{tr("Atendimentos de hoje")}</h2>
              {data.todaysAppointments.length === 0 ? (
                <EmptyState title={tr("Nenhum atendimento hoje")} description={tr("Não há pedidos pendentes ou confirmados para a data de hoje.")} />
              ) : (
                <ul className="divide-y divide-line">
                  {data.todaysAppointments.map((a) => (
                    <li key={a.id} className="flex items-center justify-between gap-3 py-3">
                      <div className="min-w-0">
                        <p className="font-semibold">
                          {a.start_time}–{a.end_time} · {a.service_name}
                        </p>
                        <p className="nl-hint truncate">
                          {a.customerName} — {a.professionalName} · {brl(a.price_cents)}
                        </p>
                      </div>
                      <StatusPill tone={TONE[a.status] ?? 'neutral'}>{statusLabel(a.status)}</StatusPill>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card as="section" className="min-w-0">
              <h2 className="mb-3 text-lg font-semibold">{tr("Próximos confirmados")}</h2>
              {data.upcoming.length === 0 ? (
                <EmptyState title={tr("Sem próximos horários confirmados")} description={tr("Os agendamentos confirmados a partir de hoje aparecerão aqui.")} />
              ) : (
                <ul className="divide-y divide-line">
                  {data.upcoming.map((a) => (
                    <li key={a.id} className="flex items-center justify-between gap-3 py-3">
                      <div className="min-w-0">
                        <p className="font-semibold">
                          {datePt(a.scheduled_date)} {a.start_time} · {a.service_name}
                        </p>
                        <p className="nl-hint truncate">
                          {a.customerName} — {a.professionalName}
                        </p>
                      </div>
                      <StatusPill tone={TONE[a.status] ?? 'neutral'}>{statusLabel(a.status)}</StatusPill>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card as="section" className="min-w-0">
              <h2 className="mb-3 text-lg font-semibold">{tr("Cancelamentos recentes")}</h2>
              {data.recentCancellations.length === 0 ? (
                <EmptyState title={tr("Nenhum cancelamento registrado")} />
              ) : (
                <ul className="divide-y divide-line">
                  {data.recentCancellations.map((c) => (
                    <li key={c.id} className="py-3">
                      <p className="font-semibold">
                        {c.service_name} — {datePt(c.scheduled_date)}
                      </p>
                      <p className="nl-hint">
                        {statusLabel(c.status)} {tr("· atualizado em")} {dateTimePt(c.updated_at)}
                        {c.cancellation_reason ? ` — motivo: ${c.cancellation_reason}` : ''}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card as="section" className="min-w-0">
              <h2 className="mb-3 text-lg font-semibold">{tr("Notificações")}</h2>
              {data.notifications.length === 0 ? (
                <EmptyState title={tr("Nenhuma notificação")} description={tr("Pedidos de alteração e eventos importantes aparecem aqui.")} />
              ) : (
                <ul className="divide-y divide-line">
                  {data.notifications.map((n) => (
                    <li key={n.id} className="py-3">
                      <p className="font-semibold">{n.title}</p>
                      <p className="nl-hint">{n.body}</p>
                      <p className="nl-hint text-xs">
                        {dateTimePt(n.created_at)}
                        {n.read_at ? ' · lida' : ' · nova'}
                      </p>
                      {n.link ? (
                        <Link to={n.link} className="text-sm font-semibold text-sage underline">
                          {tr("Ver em")} {n.link}
                        </Link>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </>
      ) : null}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="nl-card p-6">
      <p className="text-4xl font-semibold tracking-tight text-graphite tabular-nums">{value}</p>
      <p className="mt-2 text-sm text-muted">{label}</p>
    </div>
  );
}

function OverviewError({ error, onRetry }: { error: unknown; onRetry: () => void }): JSX.Element {
  if (error instanceof ApiError && error.status === 402) {
    return (
      <Alert tone="danger" title={tr("Assinatura necessária")}>
        {error.message}{' '}
        <Link className="font-semibold underline" to="/painel/plano">
          {tr("Ir para Meu plano")}
        </Link>
        .
      </Alert>
    );
  }
  if (error instanceof ApiError && error.status === 403) {
    return (
      <Alert tone="warning" title={tr("Permissão insuficiente")}>
        {tr("Seu perfil não tem acesso à visão geral deste estabelecimento.")} {error.message}
      </Alert>
    );
  }
  return (
    <Alert tone="danger" title={tr("Erro ao carregar a visão geral")}>
      <div className="space-y-2">
        <p>{error instanceof Error ? error.message : tr('Não foi possível carregar as informações.')}</p>
        <button type="button" onClick={onRetry} className="nl-btn nl-btn-secondary px-4 py-1.5">
          {tr("Tentar novamente")}
        </button>
      </div>
    </Alert>
  );
}
